import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Users, Wrench, UserCircle, BarChart3, Ship, LogOut, Shield, Euro, Clock, Calendar, Settings as SettingsIcon } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const AVATAR_FALLBACK =
  "https://api.dicebear.com/7.x/initials/svg?seed=";

export default function Perfil() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  const doLogout = () => {
    logout();
    nav("/login");
  };

  const gestion = [
    { to: "/gestion/clientes", Icon: Users, label: t("perfil.gestion.clientes"), sub: t("perfil.gestion.clientes.sub"), bg: "#E8F1FF", color: "#2D62B7" },
    { to: "/gestion/embarcaciones", Icon: Ship, label: t("perfil.gestion.embarcaciones"), sub: t("perfil.gestion.embarcaciones.sub"), bg: "#E0F2FE", color: "#0369A1" },
    { to: "/gestion/servicios", Icon: Wrench, label: t("perfil.gestion.servicios"), sub: t("perfil.gestion.servicios.sub"), bg: "#FFE6D7", color: "#C2410C" },
    { to: "/gestion/usuarios", Icon: UserCircle, label: t("perfil.gestion.usuarios"), sub: t("perfil.gestion.usuarios.sub"), bg: "#EEF0F4", color: "#475569" },
    { to: "/calendario", Icon: Calendar, label: t("perfil.gestion.agenda"), sub: t("perfil.gestion.agenda.sub"), bg: "#FFE6D7", color: "#C2410C" },
    { to: "/gestion/albaranes", Icon: BarChart3, label: t("perfil.gestion.informes"), sub: t("perfil.gestion.informes.sub"), bg: "#D5F5E0", color: "#116A34" },
    { to: "/gestion/ajustes", Icon: SettingsIcon, label: t("settings.title"), sub: t("settings.empresa.title"), bg: "#EDE9FE", color: "#7C3AED" },
  ];

  return (
    <div className="px-5 pt-8 sns-fade-up" data-testid="page-perfil">
      <div className="flex items-center justify-between mb-6">
        <h1 className="sns-title text-3xl">{t("perfil.title")}</h1>
        {/* Selector idioma rápido también en perfil (mobile) */}
        <div className="inline-flex bg-white rounded-full p-1 border border-[var(--border)] shadow-sm" data-testid="perfil-lang-switch">
          <button
            onClick={() => setLang("es")}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${lang === "es" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="perfil-lang-es"
          >🇪🇸 ES</button>
          <button
            onClick={() => setLang("en")}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${lang === "en" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="perfil-lang-en"
          >🇬🇧 EN</button>
        </div>
      </div>

      {/* Profile card */}
      <div className="sns-card p-6 mb-8 text-center">
        <img
          src={user?.avatar_url || `${AVATAR_FALLBACK}${encodeURIComponent(user?.name || "U")}`}
          alt={user?.name}
          className="w-28 h-28 rounded-full object-cover mx-auto mb-4 ring-4 ring-[var(--coral)]/15"
          data-testid="profile-avatar"
        />
        <h2 className="sns-title text-2xl" data-testid="profile-name">{user?.name}</h2>
        <p className="text-slate-500 text-sm mb-4" data-testid="profile-email">{user?.email}</p>
        <div className="inline-flex items-center gap-1.5 bg-[var(--coral)] text-white font-bold text-xs px-4 py-2 rounded-full mb-3">
          <Shield size={12} /> {isAdmin ? t("perfil.role.admin") : t("perfil.role.empleado")}
        </div>
        {isAdmin && user?.rate_per_hour > 0 && (
          <div className="text-slate-600 text-sm flex items-center justify-center gap-1.5">
            <Euro size={14} /> {t("perfil.rate")} <strong>{user.rate_per_hour}{t("perfil.rate.per_hour")}</strong>
          </div>
        )}
      </div>

      {/* Mis horas - visible for everyone */}
      <div className="text-xs font-bold text-slate-400 tracking-wider mb-3 px-2">{t("perfil.section.time")}</div>
      <div className="sns-card mb-6">
        <Link
          to="/mis-horas"
          className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
          data-testid="link-mis-horas"
        >
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#FFE6D7", color: "#C2410C" }}>
            <Clock size={20} strokeWidth={2.3} />
          </div>
          <div className="flex-1">
            <div className="font-bold">{t("perfil.team_hours")}</div>
            <div className="text-xs text-slate-500">{t("perfil.hours.sub")}</div>
          </div>
          <span className="text-slate-300 text-xl">›</span>
        </Link>
        {isAdmin && (
          <Link
            to="/plan"
            className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors border-t border-[var(--border)]"
            data-testid="link-plan"
          >
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#EDE9FE", color: "#7C3AED" }}>
              <Shield size={20} strokeWidth={2.3} />
            </div>
            <div className="flex-1">
              <div className="font-bold">{t("plan.title")}</div>
              <div className="text-xs text-slate-500">Free · Pro · Uso y facturación</div>
            </div>
            <span className="text-slate-300 text-xl">›</span>
          </Link>
        )}
      </div>

      {isAdmin && (
        <>
          <div className="text-xs font-bold text-slate-400 tracking-wider mb-3 px-2">{t("perfil.section.management")}</div>
          <div className="sns-card divide-y divide-[var(--border)] mb-6">
            {gestion.map((g) => (
              <Link
                key={g.to}
                to={g.to}
                className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                data-testid={`gestion-${g.label.toLowerCase()}`}
              >
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: g.bg, color: g.color }}>
                  <g.Icon size={20} strokeWidth={2.3} />
                </div>
                <div className="flex-1">
                  <div className="font-bold">{g.label}</div>
                  <div className="text-xs text-slate-500">{g.sub}</div>
                </div>
                <span className="text-slate-300 text-xl">›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <button
        onClick={doLogout}
        className="sns-card w-full px-5 py-4 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
        data-testid="btn-logout"
      >
        <LogOut size={18} /> {t("nav.logout")}
      </button>

    </div>
  );
}
